import * as dates from "@client/dates";
import * as orm from "@client/features/orm";
import {Results, diets, useCalculator} from "@client/features/planner";
import * as forms from "@client/forms";
import * as router from "@client/router";
import * as typography from "@client/shared/typography";
import {ImperialCaloriesContext, useSiteContext} from "@client/site/context";
import {server} from "@reactivated";
import * as React from "react";

const styles = {
    calculator: "calculator-grid",
} as const;

const calculatorProfileSchema = server.journal.nutrition.CalculatorProfileForm;

export const Calculator = router.Calculator.withHooks((props) => {
    const {request} = useSiteContext();
    const {searchParams} = new URL(request.url);

    const gender = forms.asOption(
        calculatorProfileSchema.fields.gender.options,
        props.profile?.gender,
        {missing: "m", unselected: ""},
    );
    const activity = forms.asOption(
        calculatorProfileSchema.fields.activity.options,
        props.profile?.activity,
        {missing: "1.2", unselected: ""},
    );

    const initial = {
        weight: props.profile?.weight ?? "81.6466",
        height: props.profile?.height ?? "182.88",
        gender: gender.value,
        activity: activity.value,
        is_imperial: props.profile?.is_imperial ?? true,
        date_of_birth:
            props.profile?.date_of_birth ??
            dates.formatISO(dates.subYears(new Date(), 25), {
                representation: "date",
            }),
        body_fat: props.profile?.body_fat ?? null,
        diet:
            searchParams.get("dietType") == "leangains"
                ? ("LEANGAINS" as const)
                : ("GENERAL" as const),
    };

    const calculatorForm = forms.useForm(calculatorProfileSchema, {
        initial: initial,
        drafts: {gender: gender.draft, activity: activity.draft},
    });

    const prevDietRef = React.useRef(calculatorForm.values.diet);
    const prevImperialRef = React.useRef(calculatorForm.values.is_imperial);

    React.useEffect(() => {
        if (prevDietRef.current !== calculatorForm.values.diet) {
            void props.analytics.track("CHANGE_CALCULATOR_TYPE", {
                diet: calculatorForm.values.diet,
            });
            prevDietRef.current = calculatorForm.values.diet;
        }
        if (prevImperialRef.current !== calculatorForm.values.is_imperial) {
            void props.analytics.track("CHANGE_CALCULATOR_IS_IMPERIAL", {
                is_imperial: calculatorForm.values.is_imperial === true,
            });
            prevImperialRef.current = calculatorForm.values.is_imperial;
        }
    }, [calculatorForm.values.diet, calculatorForm.values.is_imperial]);

    const selectedDiet = calculatorForm.values.diet;

    const profile = {
        ...calculatorForm.values,
        use_kilojoules: false,
        // The form carries no unit until one is chosen, and the macros below
        // need one now. `useIsImperial` answers the same question the same way.
        is_imperial: calculatorForm.values.is_imperial ?? true,
    };
    const {setIsImperial} = React.useContext(ImperialCaloriesContext);

    React.useEffect(() => {
        setIsImperial(profile.is_imperial);

        return () => {
            setIsImperial(null);
        };
    }, [profile.is_imperial]);

    const general = useCalculator({
        profile,
        formSchema: server.journal.nutrition.GeneralCalculatorForm,
        diet: diets.general,
    });
    const leangains = useCalculator({
        profile,
        formSchema: server.journal.nutrition.LeangainsCalculatorForm,
        diet: diets.leangains,
    });
    const keto = useCalculator({
        profile,
        formSchema: server.journal.nutrition.KetoCalculatorForm,
        diet: diets.keto,
    });

    const selected =
        selectedDiet == "GENERAL"
            ? general
            : selectedDiet == "LEANGAINS"
              ? leangains
              : keto;

    return {
        selected,
        isAuthenticated: props.profile != null,
        calculatorForm,
        profile,
    };
})
    .withIndex((props) => (
        <div className={styles.calculator}>
            <div>
                <typography.Title>Choose diet</typography.Title>
                <forms.Field noLabel field={props.calculatorForm.fields.diet} />
            </div>
            <div>
                <typography.Title>Use metric or imperial</typography.Title>
                <forms.Field noLabel field={props.calculatorForm.fields.is_imperial} />
            </div>
            <div>
                <typography.Title>Profile</typography.Title>
                <forms.Fields
                    form={props.calculatorForm}
                    exclude={["diet", "is_imperial"]}
                />
            </div>
            <div>
                <typography.Title>Settings</typography.Title>
                {props.selected.presets}
                {props.selected.settings}
            </div>
            <div>
                <typography.Title>Results</typography.Title>
                <Results
                    result={props.selected.results}
                    useKilojoules={props.profile.use_kilojoules}
                />
            </div>
        </div>
    ))
    .withActions((props) => [
        props.isAuthenticated
            ? {
                  icon: "save",
                  title: "Save",
                  onClick: async () => {
                      if (!forms.ready(props.calculatorForm, props.selected.form)) {
                          return;
                      }
                      const response = await server.journal.day.target_planner({
                          targets: props.selected.results.targets.map((t) => ({
                              name: t.name,
                              calories_target: t.calories_target,
                              protein_target: t.protein_target,
                              fat_target: t.fat_target,
                              carbs_target: t.carbs_target,
                              net_carbs_target: null,
                          })),
                          assignments: props.selected.results.assignments.map((a) => ({
                              ...a,
                          })),
                      });

                      if (response.type === "success") {
                          await props.dispatch(orm.fetchProfile());
                          props.notify("Targets saved");
                          void router.push("planner.index", {});
                      }
                  },
              }
            : {
                  icon: "save",
                  title: "Save",
                  link: router.reverse("login.index", {}),
              },
    ])
    .withOptions(() => ({
        title: "Calculator",
    }));
